import memoize from 'memoizee';
import { contract as currentContract, isContractDataLoaded } from '@/contract';
import { arrayToHashmap, flattenArray, uniqBy } from '@/utils/Array';
import { multiCall } from '../Calls';
import { sleepUntil } from '@/utils/Async';
import { liquiditygauge_abi, liquiditygauge_abi_v2, liquiditygauge_v3, liquiditygauge_v4, isFactoryAbiDataLoaded } from '@/constants/abis';
import { getNonFactoPools, getFactoryV2Pools, getFactoryCryptoPools, isFactoryPoolDataLoaded } from '@/constants/pools';
import getPricesForAssets from '@/utils/data/getPricesForAssets';

/**
 * Retrieves the amount of claimable additional rewards per gauge; it works for
 * any situation described in the pools config: active rewards, old inactive rewards,
 * any number of extra rewards, and liquidity gauges v1 and v2.
 *
 * Returns object of shape { compound: [{ name, amount, price }, …], … }
 */
const getClaimableAdditionalRewards = memoize(async (userAddress = '', includeFactoryV2 = false) => {
  if (userAddress === "") return [];
  await sleepUntil(() => isFactoryAbiDataLoaded() && isFactoryPoolDataLoaded() && isContractDataLoaded());

  const nonFactoPools = getNonFactoPools();
  const allPools = includeFactoryV2 ? nonFactoPools.concat(getFactoryV2Pools()).concat(getFactoryCryptoPools()) : nonFactoPools;
  const poolsWithAdditionalRewards = allPools.filter(({ additionalRewards, oldAdditionalRewards }) => (
    additionalRewards.length > 0 ||
    oldAdditionalRewards.length > 0
  ));

  const additionalRewardsAssetsInfo = uniqBy(flattenArray(poolsWithAdditionalRewards.map(({
    additionalRewards,
    oldAdditionalRewards,
  }) => (
    [...additionalRewards, ...oldAdditionalRewards].map(({ name, rewardTokenCoingeckoId }) => ({
      name,
      coingeckoId: rewardTokenCoingeckoId,
    }))
  ))), 'name');

  const additionalRewardsAssetsCoingeckoIds = additionalRewardsAssetsInfo.map(({ coingeckoId }) => coingeckoId).filter((id) => !!id);
  const additionalRewardsAssetsPrices = (
    additionalRewardsAssetsCoingeckoIds.length > 0 ? (
      await getPricesForAssets(additionalRewardsAssetsCoingeckoIds)
        .then((prices) => (
          arrayToHashmap(Object.entries(prices).map(([coingeckoId, price]) => [
            additionalRewardsAssetsInfo.find((assetInfo) => assetInfo.coingeckoId === coingeckoId).name,
            price,
          ]))
        ))
    ) : (
      []
    )
  );

  const callsConfig = flattenArray(flattenArray(poolsWithAdditionalRewards.map(({
    id,
    gaugeVersion,
    addresses: { gauge: gaugeAddress },
    additionalRewards,
    oldAdditionalRewards,
    oldAdditionalRewardsGaugeVersion,
    oldAdditionalRewardsAreForGauge,
  }) => (
    [
      ...(additionalRewards || []).map((o) => ({ ...o, isOld: false })),
      ...(oldAdditionalRewards || []).map((o) => ({ ...o, isOld: true })),
      // ...(oldAdditionalRewardsAreForGauge ? [] : oldAdditionalRewards), // Don't bother with old rewards on old gauges, only old rewards on the same gauge
    ]
      .map(({ name, rewardTokenAddress, rewardTokenDecimals, isOld, ...factoV2Props }) => {
        const effectiveGaugeVersion = isOld ? oldAdditionalRewardsGaugeVersion : gaugeVersion;
        if (effectiveGaugeVersion === 3) {
          const effectiveGaugeAddress = (isOld && oldAdditionalRewardsAreForGauge) ? oldAdditionalRewardsAreForGauge : gaugeAddress;
          return [{
            contract: new currentContract.web3.eth.Contract(liquiditygauge_v3, effectiveGaugeAddress),
            methodName: 'claimable_reward_write',
            params: [userAddress, rewardTokenAddress],
            metaData: {
              poolId: id,
              name,
              decimals: rewardTokenDecimals,
              type: 'claimable-rewards',
              gaugeAddress: effectiveGaugeAddress,
            },
          }];
        } else if (effectiveGaugeVersion === 2) {
          const isFactoV2Pool = typeof factoV2Props.tokenAddress !== 'undefined';
          const effectiveGaugeAddress = (
            isFactoV2Pool ? factoV2Props.gaugeAddress :
              (isOld && oldAdditionalRewardsAreForGauge) ? oldAdditionalRewardsAreForGauge :
                gaugeAddress
          );
          return [{
            contract: new currentContract.web3.eth.Contract(liquiditygauge_abi_v2, effectiveGaugeAddress),
            methodName: 'claimable_reward',
            params: [userAddress, isFactoV2Pool ? factoV2Props.tokenAddress : rewardTokenAddress],
            metaData: {
              poolId: id,
              name: isFactoV2Pool ? factoV2Props.symbol : name,
              decimals: isFactoV2Pool ? factoV2Props.decimals : rewardTokenDecimals,
              type: 'claimable-rewards',
              rewardTokenPrice: isFactoV2Pool ? factoV2Props.tokenPrice : undefined,
              gaugeAddress: effectiveGaugeAddress,
            },
          }];

        } else if (effectiveGaugeVersion === 4) {
          const effectiveGaugeAddress = (isOld && oldAdditionalRewardsAreForGauge) ? oldAdditionalRewardsAreForGauge : gaugeAddress;
          return [{
            contract: new currentContract.web3.eth.Contract(liquiditygauge_v4, effectiveGaugeAddress),
            methodName: 'claimable_reward_write',
            params: [userAddress, rewardTokenAddress],
            metaData: {
              poolId: id,
              name,
              decimals: rewardTokenDecimals,
              type: 'claimable-rewards',
              gaugeAddress: effectiveGaugeAddress,
            },
          }];

        } else {
          const effectiveGaugeAddress = (isOld && oldAdditionalRewardsAreForGauge) ? oldAdditionalRewardsAreForGauge : gaugeAddress;
          const contract = new currentContract.web3.eth.Contract(liquiditygauge_abi, effectiveGaugeAddress);
          return [{
            contract,
            methodName: 'claimable_reward',
            params: [userAddress],
            metaData: {
              poolId: id,
              name,
              decimals: 18, // Old reward tokens all have 18 decimals
              type: 'total-rewards',
              gaugeAddress: effectiveGaugeAddress,
            },
          }, {
            contract,
            methodName: 'claimed_rewards_for',
            params: [userAddress],
            metaData: {
              poolId: id,
              name,
              decimals: 18, // Old reward tokens all have 18 decimals
              type: 'claimed-rewards',
              gaugeAddress: effectiveGaugeAddress,
            },
          }];
        }
      })
  ))));

  const rewardsData = await multiCall(callsConfig);

  const claimableAdditionalRewards = rewardsData.reduce((accu, callResult) => {
    const {
      data,
      metaData: {
        poolId,
        name,
        decimals,
        type,
        rewardTokenPrice,
        gaugeAddress,
      },
    } = callResult;

    if (type === 'claimable-rewards') {
      return accu.concat({
        poolId,
        name,
        claimableRewards: data / (10 ** decimals),
        rewardTokenPrice,
        gaugeAddress,
      });
    }

    if (type === 'total-rewards') {
      const totalRewards = data;
      const claimedRewards = rewardsData.find(({ metaData }) => (
        metaData.poolId === poolId &&
        metaData.name === name &&
        metaData.type === 'claimed-rewards'
      )).data;

      return accu.concat({
        poolId,
        name,
        claimableRewards: (totalRewards - claimedRewards) / (10 ** decimals),
        gaugeAddress,
      });
    }

    return accu;
  }, []);

  const claimableAdditionalRewardsPerGauge =
    claimableAdditionalRewards.reduce((accu, { poolId, name, claimableRewards, rewardTokenPrice, gaugeAddress }) => {
      const poolRewards = accu[poolId] || [];

      accu[poolId] = poolRewards.concat({
        name,
        amount: claimableRewards,
        price: (
          additionalRewardsAssetsPrices[name] ||
          rewardTokenPrice // Facto pools have this prop retrieved from the api
        ),
        gaugeAddress,
      });

      return accu;
    }, {});

  return claimableAdditionalRewardsPerGauge;
}, {
  promise: true,
  maxAge: 10 * 60 * 1000, // 10m
  length: 2, // Take into account second optional param for cache busting
});

export default getClaimableAdditionalRewards;
